import type { ReactNode } from "react";

import { BlockIcon, BlockType } from "@/components/PowerBlock/Blocks";
import type { TraceSpanLike } from "@/components/TraceDetail/SpanDetails/types";
import { Dataset, Evaluation } from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import { formatTimestampUTC } from "@/utils/timestamp";

const MS_PER_MINUTE = 60_000;
const MS_PER_SECOND = 1_000;

/** OpenTelemetry / Vercel AI `ai.operationId` and related attribute values for tool spans. */
enum VercelAiOperationId {
  TOOL_CALL = "ai.toolCall",
}

const OTEL_ATTR_TOOL_NAME = "ai.toolCall.name";

enum VercelLLMSessionOperation {
  GenerateText = "ai.generateText",
  StreamText = "ai.streamText",
  GenerateObject = "ai.generateObject",
  StreamObject = "ai.streamObject",
}

enum VercelLLMCallOperation {
  GenerateText = "ai.generateText.doGenerate",
  StreamText = "ai.streamText.doStream",
  GenerateObject = "ai.generateObject.doGenerate",
  StreamObject = "ai.streamObject.doStream",
}

enum VercelEmbeddingSessionOperation {
  Embed = "ai.embed",
  EmbedMany = "ai.embedMany",
}

enum VercelEmbeddingCallOperation {
  Embed = "ai.embed.doEmbed",
  EmbedMany = "ai.embedMany.doEmbed",
}

const VERCEL_LLM_SESSION_OPERATION_IDS = new Set<string>(Object.values(VercelLLMSessionOperation));

const VERCEL_LLM_CALL_OPERATION_IDS = new Set<string>(Object.values(VercelLLMCallOperation));

const VERCEL_EMBEDDING_SESSION_OPERATION_IDS = new Set<string>(
  Object.values(VercelEmbeddingSessionOperation),
);

const VERCEL_EMBEDDING_CALL_OPERATION_IDS = new Set<string>(
  Object.values(VercelEmbeddingCallOperation),
);

const TOOL_NAME_ATTRIBUTE_KEYS = [
  "pl.tool_name",
  "gen_ai.tool.name",
  "tool.name",
  "function_name",
  OTEL_ATTR_TOOL_NAME,
] as const;

const getAttributeString = (span: TraceSpanLike, key: string) => {
  const value = span.attributes?.[key];
  return typeof value === "string" && value.trim() ? value : undefined;
};

const hasValue = (value: unknown) => value !== undefined && value !== null;

const TOOL_ATTRIBUTE_KEYS = {
  input: [
    "gen_ai.tool.call.arguments",
    "ai.toolCall.args",
    "function_input",
    "tool_arguments",
    "input.value",
  ],
  output: [
    "gen_ai.tool.call.result",
    "ai.toolCall.result",
    "function_output",
    "tool_response",
    "output.value",
  ],
} as const;

const hasToolSignal = (span: TraceSpanLike) =>
  getAttributeString(span, "node_type") === "CODE_EXECUTION" ||
  getAttributeString(span, "node_type") === "TOOL_EXECUTION" ||
  Boolean(
    getAttributeString(span, "gen_ai.tool.name") ||
      getAttributeString(span, "tool.name") ||
      getAttributeString(span, "function_name"),
  );

const getSpanToolAttribute = (span: TraceSpanLike, type: keyof typeof TOOL_ATTRIBUTE_KEYS) => {
  if (!hasToolSignal(span)) {
    return undefined;
  }

  for (const key of TOOL_ATTRIBUTE_KEYS[type]) {
    const value = span.attributes?.[key];
    if (hasValue(value)) {
      return { key, value };
    }
  }

  return undefined;
};

export const getSpanToolInput = (span: TraceSpanLike): unknown =>
  getSpanToolAttribute(span, "input")?.value;

export const getSpanToolOutput = (span: TraceSpanLike): unknown =>
  getSpanToolAttribute(span, "output")?.value;

export const getSpanRenderedToolAttributeKeys = (span: TraceSpanLike): Set<string> => {
  const keys = new Set<string>();

  for (const type of ["input", "output"] as const) {
    const key = getSpanToolAttribute(span, type)?.key;
    if (key) {
      keys.add(key);
    }
  }

  return keys;
};

// Node types whose function_name is a display label ("Prompt Template"), not a tool identity
const NON_TOOL_NODE_TYPES = new Set([
  "PROMPT_TEMPLATE",
  "LLM_SESSION",
  "LLM_CALL",
  "WORKFLOW",
  "EVAL",
  "HTTP_CLIENT",
]);

export const getSpanToolName = (span: TraceSpanLike): string | undefined => {
  const nodeType = getAttributeString(span, "node_type");
  for (const key of TOOL_NAME_ATTRIBUTE_KEYS) {
    if (key === "function_name" && nodeType && NON_TOOL_NODE_TYPES.has(nodeType)) {
      continue;
    }
    const value = getAttributeString(span, key);
    if (value) {
      return value.trim();
    }
  }

  const displayName = span.name.match(/^Tool:\s*(.+)$/i)?.[1]?.trim();
  return displayName || undefined;
};

const formatFetchSpanName = (name: string) => {
  const match = /^fetch\s+(\w+)\s+(https?:\/\/\S+)$/i.exec(name);
  if (!match) {
    return name;
  }

  const [, method, rawUrl] = match;

  try {
    const url = new URL(rawUrl);
    if (url.hostname === "api.openai.com" && url.pathname === "/v1/responses") {
      return "OpenAI Responses API";
    }

    return `${method.toUpperCase()} ${url.hostname}${url.pathname}`;
  } catch {
    return name;
  }
};

const isNextJsSpan = (span: TraceSpanLike) =>
  getAttributeString(span, "otel.scope.name") === "next.js";

const isFetchClientSpan = (span: TraceSpanLike) =>
  span.name.startsWith("fetch ") ||
  Boolean(getAttributeString(span, "http.url")) ||
  getAttributeString(span, "next.span_type") === "AppRender.fetch";

const isEndpointLikeSpan = (span: TraceSpanLike) => {
  if (getAttributeString(span, "http.route") || getAttributeString(span, "next.route")) {
    return true;
  }

  if (!isNextJsSpan(span)) {
    return false;
  }

  return /^(GET|POST|PUT|PATCH|DELETE)\s+/i.test(span.name) || span.name.includes("api route");
};

const isNextJsFrameworkSpan = (span: TraceSpanLike) =>
  isNextJsSpan(span) && !isEndpointLikeSpan(span) && !isFetchClientSpan(span);

export const getSpanDisplayType = (span: TraceSpanLike): string | undefined => {
  const operationId = getAttributeString(span, "ai.operationId");
  if (operationId === VercelAiOperationId.TOOL_CALL) {
    return "CODE_EXECUTION";
  }

  if (
    operationId &&
    (VERCEL_LLM_CALL_OPERATION_IDS.has(operationId) ||
      VERCEL_EMBEDDING_CALL_OPERATION_IDS.has(operationId))
  ) {
    return "LLM_CALL";
  }

  if (
    operationId &&
    (VERCEL_LLM_SESSION_OPERATION_IDS.has(operationId) ||
      VERCEL_EMBEDDING_SESSION_OPERATION_IDS.has(operationId))
  ) {
    return "LLM_SESSION";
  }

  const explicitNodeType = getAttributeString(span, "node_type");
  if (explicitNodeType) {
    return explicitNodeType;
  }

  if (span.request_log_id) {
    return "PROMPT_TEMPLATE";
  }

  if (isFetchClientSpan(span)) {
    return "HTTP_CLIENT";
  }

  if (isEndpointLikeSpan(span)) {
    return "ENDPOINT";
  }

  if (isNextJsFrameworkSpan(span)) {
    return "FRAMEWORK";
  }

  return undefined;
};

export const getSpanDisplayName = (span: TraceSpanLike) => {
  const operationId = getAttributeString(span, "ai.operationId");
  const toolName = getAttributeString(span, OTEL_ATTR_TOOL_NAME);

  if (operationId === VercelAiOperationId.TOOL_CALL) {
    return toolName ? `Tool: ${toolName}` : "Tool";
  }

  if (operationId && VERCEL_EMBEDDING_CALL_OPERATION_IDS.has(operationId)) {
    return "Embedding call";
  }

  if (operationId && VERCEL_EMBEDDING_SESSION_OPERATION_IDS.has(operationId)) {
    return "Embedding";
  }

  if (operationId && VERCEL_LLM_CALL_OPERATION_IDS.has(operationId)) {
    return "LLM call";
  }

  if (operationId && VERCEL_LLM_SESSION_OPERATION_IDS.has(operationId)) {
    return "LLM session";
  }

  if (span.name.startsWith("fetch ")) {
    return formatFetchSpanName(span.name);
  }

  if (span.name.startsWith("executing api route (app) ")) {
    return span.name.replace("executing api route (app) ", "Execute route ");
  }

  return span.name;
};

export const formatDate = (dateString: string) =>
  formatTimestampUTC(dateString, { includeSeconds: true, showRelative: false });

const SPAN_TYPE_LABELS: Record<string, string> = {
  LLM_CALL: "LLM Call",
  LLM_SESSION: "LLM Session",
  TOOL_EXECUTION: "Tool Execution",
  EVAL: "Eval",
  HTTP_CLIENT: "HTTP Client",
  WORKFLOW: "Workflow",
};

export const formatSpanTypeLabel = (value: string): string =>
  SPAN_TYPE_LABELS[value] ??
  value
    .toLowerCase()
    .replaceAll("_", " ")
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const formatDurationLabel = (durationMs: number) => {
  if (durationMs >= MS_PER_MINUTE) {
    const minutes = Math.floor(durationMs / MS_PER_MINUTE);
    const seconds = Math.round((durationMs % MS_PER_MINUTE) / MS_PER_SECOND);
    if (seconds === 0) {
      return `${minutes}min`;
    }
    return `${minutes}min ${seconds}sec`;
  } else if (durationMs >= MS_PER_SECOND) {
    return `${(durationMs / MS_PER_SECOND).toFixed(2)}s`;
  }
  return `${Math.round(durationMs)}ms`;
};

export const getNodeTypeConfig = (
  nodeType: string,
  iconClass: string,
): { icon: ReactNode; bgColor: string } => {
  const nodeTypeToBlockType: Record<string, BlockType> = {
    PROMPT_TEMPLATE: BlockType.PROMPT_TEMPLATE,
    LLM_CALL: BlockType.PROMPT_TEMPLATE,
    WORKFLOW: BlockType.WORKFLOW,
    EVAL: BlockType.CODE_EXECUTION,
    CODE_EXECUTION: BlockType.CODE_EXECUTION,
    TOOL_EXECUTION: BlockType.CODE_EXECUTION,
    ENDPOINT: BlockType.ENDPOINT,
    MCP: BlockType.MCP,
    CONVERSATION_SIMULATOR: BlockType.CONVERSATION_SIMULATOR,
    VARIABLE: BlockType.VARIABLE,
    PARSE_VALUE: BlockType.PARSE_VALUE,
    CONTAINS: BlockType.CONTAINS,
    JSON_PATH: BlockType.JSON_PATH,
    REGEX: BlockType.REGEX,
    COALESCE: BlockType.COALESCE,
    COMBINE_COLUMNS: BlockType.COMBINE_COLUMNS,
    LLM_ASSERTION: BlockType.LLM_ASSERTION,
    ASSERT_VALID: BlockType.ASSERT_VALID,
    AI_DATA_EXTRACTION: BlockType.AI_DATA_EXTRACTION,
    COSINE_SIMILARITY: BlockType.COSINE_SIMILARITY,
    WHILE_LOOP: BlockType.WHILE_LOOP,
    FOR_LOOP: BlockType.FOR_LOOP,
    COUNT: BlockType.COUNT,
    TRAJECTORY: BlockType.TRAJECTORY,
    ABSOLUTE_NUMERIC_DISTANCE: BlockType.ABSOLUTE_NUMERIC_DISTANCE,
    APPLY_DIFF: BlockType.APPLY_DIFF,
    LLM_SESSION: BlockType.CONVERSATION_SIMULATOR,
    HTTP_CLIENT: BlockType.ENDPOINT,
    FRAMEWORK: BlockType.ENDPOINT,
  };

  const blockType = nodeTypeToBlockType[nodeType];
  const IconComponent = blockType ? BlockIcon[blockType] : null;

  switch (nodeType) {
    case "PROMPT_TEMPLATE":
    case "LLM_CALL":
      return {
        icon: IconComponent ? <IconComponent className={cn(iconClass, "text-amber-600")} /> : null,
        bgColor: "bg-amber-50",
      };
    case "LLM_SESSION":
      return {
        icon: IconComponent ? <IconComponent className={cn(iconClass, "text-orange-600")} /> : null,
        bgColor: "bg-orange-50",
      };
    case "WORKFLOW":
      return {
        icon: IconComponent ? <IconComponent className={cn(iconClass, "text-violet-600")} /> : null,
        bgColor: "bg-violet-50",
      };
    case "DATASET":
      return {
        icon: <Dataset className={cn(iconClass, "text-indigo-600")} />,
        bgColor: "bg-indigo-50",
      };
    case "REPORT":
      return {
        icon: <Evaluation className={cn(iconClass, "text-emerald-600")} />,
        bgColor: "bg-emerald-50",
      };
    case "WHILE_LOOP":
    case "FOR_LOOP":
      return {
        icon: IconComponent ? <IconComponent className={cn(iconClass, "text-cyan-600")} /> : null,
        bgColor: "bg-cyan-50",
      };
    case "EVAL":
      return {
        icon: <Evaluation className={cn(iconClass, "text-emerald-600")} />,
        bgColor: "bg-emerald-50",
      };
    case "CODE_EXECUTION":
    case "TOOL_EXECUTION":
      return {
        icon: IconComponent ? (
          <IconComponent className={cn(iconClass, "text-emerald-600")} />
        ) : null,
        bgColor: "bg-emerald-50",
      };
    case "ENDPOINT":
    case "HTTP_CLIENT":
      return {
        icon: IconComponent ? <IconComponent className={cn(iconClass, "text-blue-600")} /> : null,
        bgColor: "bg-blue-50",
      };
    case "FRAMEWORK":
      return {
        icon: IconComponent ? <IconComponent className={cn(iconClass, "text-slate-600")} /> : null,
        bgColor: "bg-slate-50",
      };
    case "MCP":
      return {
        icon: IconComponent ? <IconComponent className={cn(iconClass, "text-violet-600")} /> : null,
        bgColor: "bg-violet-50",
      };
    case "CONVERSATION_SIMULATOR":
      return {
        icon: IconComponent ? <IconComponent className={cn(iconClass, "text-pink-600")} /> : null,
        bgColor: "bg-pink-50",
      };
    case "VARIABLE":
      return {
        icon: IconComponent ? <IconComponent className={cn(iconClass, "text-teal-600")} /> : null,
        bgColor: "bg-teal-50",
      };
    case "INPUT_VARIABLE_SET": {
      const VariableIcon = BlockIcon[BlockType.VARIABLE];
      return {
        icon: VariableIcon ? <VariableIcon className={cn(iconClass, "text-teal-600")} /> : null,
        bgColor: "bg-teal-50",
      };
    }
    case "PARSE_VALUE":
      return {
        icon: IconComponent ? <IconComponent className={cn(iconClass, "text-lime-600")} /> : null,
        bgColor: "bg-lime-50",
      };
    case "CONTAINS":
      return {
        icon: IconComponent ? (
          <IconComponent className={cn(iconClass, "text-fuchsia-600")} />
        ) : null,
        bgColor: "bg-fuchsia-50",
      };
    case "JSON_PATH":
      return {
        icon: IconComponent ? <IconComponent className={cn(iconClass, "text-sky-600")} /> : null,
        bgColor: "bg-sky-50",
      };
    case "REGEX":
      return {
        icon: IconComponent ? <IconComponent className={cn(iconClass, "text-rose-600")} /> : null,
        bgColor: "bg-rose-50",
      };
    case "COALESCE":
      return {
        icon: IconComponent ? (
          <IconComponent className={cn(iconClass, "text-neutral-600")} />
        ) : null,
        bgColor: "bg-neutral-50",
      };
    case "COMBINE_COLUMNS":
      return {
        icon: IconComponent ? (
          <IconComponent className={cn(iconClass, "text-emerald-600")} />
        ) : null,
        bgColor: "bg-emerald-50",
      };
    case "LLM_ASSERTION":
      return {
        icon: IconComponent ? (
          <IconComponent className={cn(iconClass, "text-emerald-600")} />
        ) : null,
        bgColor: "bg-emerald-50",
      };
    case "ASSERT_VALID":
      return {
        icon: IconComponent ? (
          <IconComponent className={cn(iconClass, "text-emerald-600")} />
        ) : null,
        bgColor: "bg-emerald-50",
      };
    case "AI_DATA_EXTRACTION":
      return {
        icon: IconComponent ? <IconComponent className={cn(iconClass, "text-amber-600")} /> : null,
        bgColor: "bg-amber-50",
      };
    case "COSINE_SIMILARITY":
      return {
        icon: IconComponent ? <IconComponent className={cn(iconClass, "text-violet-600")} /> : null,
        bgColor: "bg-violet-50",
      };
    case "COUNT":
      return {
        icon: IconComponent ? <IconComponent className={cn(iconClass, "text-slate-600")} /> : null,
        bgColor: "bg-slate-50",
      };
    case "TRAJECTORY":
      return {
        icon: IconComponent ? <IconComponent className={cn(iconClass, "text-cyan-600")} /> : null,
        bgColor: "bg-cyan-50",
      };
    case "ABSOLUTE_NUMERIC_DISTANCE":
      return {
        icon: IconComponent ? <IconComponent className={cn(iconClass, "text-stone-600")} /> : null,
        bgColor: "bg-stone-50",
      };
    case "APPLY_DIFF":
      return {
        icon: IconComponent ? (
          <IconComponent className={cn(iconClass, "text-neutral-600")} />
        ) : null,
        bgColor: "bg-neutral-50",
      };
    default: {
      const DefaultIcon = BlockIcon[BlockType.CODE_EXECUTION];
      return {
        icon: DefaultIcon ? <DefaultIcon className={cn(iconClass, "text-gray-600")} /> : null,
        bgColor: "bg-gray-50",
      };
    }
  }
};
